import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../services/api';

import './user.css';
import 'react-toastify/dist/ReactToastify.css';

import UserAddEdit from '../user-add-edit/UserAddEdit';
import toastOptions from '../../../utils/toastOptions';
import { authenticationService } from '../../../services/auth.service';
import UserLogin from '../../../models/UserLogin';
import { UserSystem } from '../../../models/enums/UserSystem';
import { faChartSimple, faPencil, faPlus, faSearch, faTrash, faUnlock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IDeleteDTO from '../../../models/IDeleteDTO';
import DialogConfirm from '../../../components/DialogConfirm';
import { displayDateTime } from '../../../utils/masks';
import { TablePage } from '../../../containers';

interface UserListRow {
  id: number;
  name: string;
  user: string;
  email: string;
  roles: string[];
  lastLoginDate: Date;
}

export default function UserList() {
  const [refresh, doRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userId, setId] = useState<number>();
  const [add, doAdd] = useState(false);
  const [isReadOnly, setReadOnly] = useState<boolean>(true);
  const [termSearch, setTermSearch] = useState<string>('');
  const [delayedTermSearch, setDelayedTermSearch] = useState<string>('');
  const [systemSearch, setSystemSearch] = useState<string>(UserSystem.DASHBOARD);
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [dialogText, setDialogText] = useState<string[]>([]);
  const [confirmTextLabel, setConfirmTextLabel] = useState<string>('');
  const [pageNo, setPageNo] = useState<number>(1);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  var userSelected = useRef(0);
  var userResetPassword = useRef<{ id: number; userName: string; action: 'Delete' | 'ResetPassword' | '' }>({ id: 0, userName: '', action: '' });
  
  const columns = [
    {
      name: 'Nome',
      sortable: true,
      grow: 2,
      cell: (row: UserListRow) => (<div className='uk-flex uk-flex-column'>
        <span>{row.name}</span>
        <span className='uk-text-nowrap uk-text-muted'>{row.email}</span>
      </div>
      )
    },
    {
      name: 'Usuário',
      selector: 'user',
      cell: (row: UserListRow) => <div className='uk-flex uk-flex-column'>
        <span>{row.user}</span>
        <span className='uk-text-nowrap uk-text-muted uk-text-bold'>{authenticationService.showRole(row.roles)}</span>
      </div>
    },
    {
      name: 'Último Login',
      cell: (row: UserListRow) => <span className='uk-text-italic uk-text-xsmall'>{`${row.lastLoginDate ? displayDateTime(row.lastLoginDate) : 'N/A'}`}</span>
    },
    {
      name: 'Ações',
      button: true,
      sortable: false,
      cell: (row: UserListRow) => (
        <div>
          {
            isReadOnly ? (authenticationService.currentUserValue as UserLogin).email === row.email &&
              <a href="#" uk-tooltip="title: Visualizar"
                className="uk-margin-right"
                onClick={(ev) => { ev.preventDefault(); handleEdit(row.id) }}
              >
                <FontAwesomeIcon icon={faSearch} />
              </a> 
            : (
              <div>
                <a href="#" 
                  uk-tooltip={`title: ${authenticationService.isAdmin() ? 'Editar' : 'Visualizar'}`}
                  className="uk-margin-right"
                  onClick={(ev) => { ev.preventDefault(); handleEdit(row.id) }}
                >
                  {authenticationService.isAdmin() ? <FontAwesomeIcon icon={faPencil} /> : <FontAwesomeIcon icon={faSearch} />}
                </a>
                {
                  authenticationService.isAdmin() && (
                    <a href="#" 
                      uk-tooltip="title: Resetar Senha"
                      className="uk-margin-right"
                      onClick={(ev) => { ev.preventDefault(); handleResetPassword(row.id, row.user) }}
                    >
                      <FontAwesomeIcon icon={faUnlock} />
                    </a>
                  )
                }
                {
                  (!isReadOnly) &&
                    <a href="#" uk-tooltip="title: Inativar"
                      className="uk-margin-right"
                      onClick={(ev) => { ev.preventDefault(); handleRemove(row) }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </a>
                }
              </div>
            )
          }
        </div>
      )
    },
  ];  
  
  useEffect(()=>{
    setReadOnly(!authenticationService.canChange());
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDelayedTermSearch(termSearch);
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [termSearch]);

  useEffect(() => {
    doRefresh((prev) => !prev);
  }, [delayedTermSearch, systemSearch]);
  
  const fetchUsers = async (page: number, limit: number) => {
    try {
      setLoading(true);
      if (delayedTermSearch) {
        const responseSearch = await api.get(`users/name/${delayedTermSearch.toUpperCase()}`, { 
          params: { page: page, limit: limit, system: systemSearch } 
        });
        return responseSearch;
      }

      const response = await api.get('users', { params: { page: page, limit: limit, system: systemSearch } });

      setPageNo(page);

      setTotalUsers(response.data.total);
      
      return response;
    } catch {
      return {data: [], total: 0}
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    doAdd(true);        
  }

  const handleEdit = async (id: number) => {
    setId(id);        
  }

  const afterHandleSubmit = () => {
    doAdd(false); 
    setId(undefined);
    doRefresh(!refresh); 
  }

  const afterCloseModal = () => {
    doAdd(false);
    setId(undefined);
  }

  const handleResetPassword = async (id: number, userName: string) => {
    setConfirmTextLabel('');
    setDialogText([`Deseja realmente resetar a senha de ${userName}?`]);
    userResetPassword.current = { id, userName, action: 'ResetPassword' };
    setIsOpenDialog(true);
  }
  
  const handleSearch = async (search: string, system: string) => {
    setSystemSearch(system);

    if (search?.length > 2 || search?.length === 0) {
      setTermSearch(search);
      return;
    }

    if (search?.length <= 2 && delayedTermSearch) {
      setTermSearch('');
    }
  }
  
  const handleRemove  = async (row: UserListRow) => {
    setConfirmTextLabel('Informe sua senha');
    setDialogText([`Deseja realmente inativar ${row.name}?`, 'Confirme sua senha se tiver certeza']);
    userSelected.current = row.id;
    userResetPassword.current = { id: 0, userName: '', action: 'Delete' };
    setIsOpenDialog(true);
  }

  const dialogClose = () => {
    setIsOpenDialog(false);
  }

  const dialogConfirmDelete = async (confirmationCode: string) => {
    if (!confirmationCode) {
      toast.warning('Informe a senha', toastOptions);
      return;
    }

    setIsOpenDialog(false);

    const dto = {} as IDeleteDTO;
    dto.confirmationCode = confirmationCode;
    dto.idDelete = +(userSelected.current);

    try {
      await api.post(`users/delete`, dto);
      toast.success('Usuário inativado com sucesso!', toastOptions);
      doRefresh(!refresh);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      cleanAction();
    }
  }

  const dialogConfirmResetPassword = async () => {
    setIsOpenDialog(false);
    toast.info('Solicitação iniciada...', toastOptions);
    try {
      await api.post(`account/request-password/${userResetPassword.current.id}`);
      toast.dismiss();
      toast.success(`O e-mail do usuário ${userResetPassword.current.userName} para criação da nova senha foi enviado!`, toastOptions);
    } catch (e) {
      toast.dismiss();
      toast.warning('Problemas ao enviar e-mail!', toastOptions);
    } finally {
      cleanAction();
    }
  }

  const cleanAction = () => {
    userSelected.current = 0;
    userResetPassword.current = { id: 0, userName: '', action: '' };
    setIsOpenDialog(false);
  }

  const handleOptions = () => {
    return (<div className="uk-margin-top uk-grid-small" uk-grid="true" id="search-context">
      <div className="uk-search uk-search-default uk-width-1-2@m">
        <span className="uk-search-icon-flip" uk-search-icon="true"></span>
        <input 
          className="uk-search-input uk-input uk-form-small" 
          type="search" 
          placeholder="Pesquise por nome..."
          onChange={(ev)=>{ handleSearch(ev.target.value, systemSearch); }}
        />
      </div> 
      {authenticationService.isAdmin() && (
        <div className="uk-width-1-4@s">
          <div className="uk-form-controls">
            <label className="uk-form-label" htmlFor="role">Sistema</label>
            <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
              <select className="uk-form-small"
                id="system" 
                placeholder="Sistema"
                name="system"
                defaultValue={systemSearch}
                onChange={(ev)=>{ handleSearch(termSearch, ev.target.value); }}
              >
                <option value="">Todos</option>
                <option value={UserSystem.DASHBOARD}>Dashboard</option>
                <option value={UserSystem.CETADEL}>Cetadel</option>
              </select>
              <button 
                className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                style={{textTransform: 'none'}} 
                type="button" 
                tabIndex={-1}
              >
                <span></span>
                <span uk-icon="icon: chevron-down"></span>
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="uk-search uk-search-default uk-width-1-2@m">
        <span className="uk-badge uk-badge-primary uk-text-nowrap uk-padding-small">
          {!totalUsers ? (
            <div uk-spinner="true; ratio:0.5"></div>
          ) : (
            <span>{totalUsers} usuário{totalUsers !== 1 ? 's' : ''}</span>
          )}
        </span>
      </div>
    </div>)
  }

  return (
    <div id="page-user-list">
      <TablePage 
        title={"Usuários Ativos"}
        columns={columns}
        fetch={fetchUsers}
        refresh={refresh}
        messageEmpty={<span>Nenhum <b>usuário</b> encontrado!</span>}
        pageNo={pageNo}
        options={handleOptions()}
        extraHeaderButtons={<>{
          (!isReadOnly) && <div className="uk-flex uk-margin-small-left">
            <div className="uk-flex uk-margin-small-left">
              <button className="uk-button uk-button-small uk-button-primary" onClick={handleAddUser}>
                <div className="uk-inline-clip uk-transition-toggle">
                  <div className="uk-transition-scale-up uk-transition-opaque">
                    {`Adicionar `} <FontAwesomeIcon icon={faPlus} size='xl' />
                  </div>
                </div>
              </button>
            </div>
            <div className="uk-flex uk-margin-small-left">
              <Link to="/auditoria" className="uk-button uk-button-small uk-button-success">
                <div className="uk-inline-clip uk-transition-toggle">                                        
                  <div className="uk-transition-scale-up uk-transition-opaque">
                    Auditoria
                    <FontAwesomeIcon icon={faChartSimple} size='xl' className='uk-margin-small-left' />
                  </div>            
                </div>
              </Link>
            </div>
          </div>
        }</>}
      />
      <UserAddEdit 
        add={add} 
        userId={userId} 
        afterHandleSubmit={afterHandleSubmit}
        afterCloseModal={afterCloseModal}
      />
      <DialogConfirm 
        open={isOpenDialog} 
        text={dialogText} 
        afterClose={dialogClose}
        confirmTextLabel={confirmTextLabel}
        handleConfirmation={(confirmationCode) => userResetPassword.current.action === 'Delete' ? dialogConfirmDelete(confirmationCode) : dialogConfirmResetPassword()}
      />         
    </div>
  )
}
